import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { environmentConfig, currentEnvironment } from '../config/environment';
import { ENVIRONMENT_HEADER, Environment } from '@byte-store-control/config';

// Event dispatched when a 401 is received - allows React components to handle logout
export const SESSION_EXPIRED_EVENT = 'session-expired';

class NetworkManager {
  private static instance: NetworkManager;
  private axiosInstance: AxiosInstance;
  private environment: Environment = currentEnvironment;
  private authToken?: string;
  private brandId?: string;
  private sessionExpiredDispatched = false;

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: environmentConfig.API_BASE_URL,
      timeout: environmentConfig.API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token and environment header
    this.axiosInstance.interceptors.request.use(
      (config) => {
        if (this.authToken) {
          config.headers.Authorization = `Bearer ${this.authToken}`;
        }
        // Always send the environment header (required by API middleware)
        config.headers[ENVIRONMENT_HEADER] = this.environment;
        if (this.brandId) {
          config.headers['x-brand-id'] = this.brandId;
        }

        if (environmentConfig.ENABLE_LOGGING) {
          console.log('API Request:', config.method?.toUpperCase(), config.url);
        }
        return config;
      },
      (error) => {
        console.error('Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response) => {
        if (environmentConfig.ENABLE_LOGGING) {
          console.log('API Response:', response.status, response.config.url);
        }
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Clear the in-memory token
          this.clearAuthorizationToken();

          // Dispatch session-expired event for React components to handle logout
          // Only dispatch once per session to prevent multiple redirects
          if (!this.sessionExpiredDispatched) {
            this.sessionExpiredDispatched = true;
            window.dispatchEvent(
              new CustomEvent(SESSION_EXPIRED_EVENT, {
                detail: {
                  message: error.response?.data?.message || 'Session expired',
                  code: error.response?.data?.code || 'SESSION_EXPIRED',
                },
              })
            );
          }
        }
        console.error('Response Error:', error);
        return Promise.reject(error);
      }
    );
  }

  public static getInstance(): NetworkManager {
    if (!NetworkManager.instance) {
      NetworkManager.instance = new NetworkManager();
    }
    return NetworkManager.instance;
  }

  public setEnvironment(env: Environment): void {
    this.environment = env;
  }

  public setAuthorizationToken(token: string): void {
    this.authToken = token;
    this.sessionExpiredDispatched = false; // Reset flag on new auth
  }

  public clearAuthorizationToken(): void {
    this.authToken = undefined;
  }

  public setBrandId(brand: string): void {
    this.brandId = brand;
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.get<T>(url, config);
    return response.data;
  }

  public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data, config);
    return response.data;
  }

  public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.put<T>(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.delete<T>(url, config);
    return response.data;
  }

  public async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.patch<T>(url, data, config);
    return response.data;
  }
}

export default NetworkManager.getInstance();
